
import { defineComponent } from "vue";
import grapesjs from "grapesjs";
import gjsBlocksBasic from "grapesjs-blocks-basic";
import "grapesjs/dist/css/grapes.min.css";
import {
  getGhlSite,
  previewGhlSitePageUrl,
  publishGhlSitePage,
  updateGhlSitePage
} from "@/api/ghlSites.api.js";

function cloneProjectData(raw: Record<string, unknown>) {
  return JSON.parse(JSON.stringify(raw)) as Record<string, unknown>;
}

function draftCacheKey(siteId: string | number, pageId: string | number) {
  return `ghl-site-draft:${siteId}:${pageId}`;
}

function normalizeProjectData(raw: unknown) {
  if (!raw) {
    return {};
  }
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch (_err) {
      return {};
    }
  }
  if (typeof raw === "object") {
    return raw as Record<string, unknown>;
  }
  return {};
}

function stripProjectData(raw: Record<string, unknown>) {
  const data: Record<string, unknown> = {};
  if (Array.isArray(raw.pages) && raw.pages.length) {
    data.pages = raw.pages;
  }
  if (Array.isArray(raw.styles)) {
    data.styles = raw.styles;
  }
  if (Array.isArray(raw.assets)) {
    data.assets = raw.assets;
  }
  if (Array.isArray(raw.symbols)) {
    data.symbols = raw.symbols;
  }
  return data;
}

function normalizeTextComponentForLoad(node: any) {
  if (!node || typeof node !== "object") {
    return;
  }
  const type = String(node.type || "").toLowerCase();
  if (type === "text") {
    const children = Array.isArray(node.components) ? node.components : [];
    const fromTextNodes = children
      .filter((child: any) => child && String(child.type || "").toLowerCase() === "textnode")
      .map((child: any) => String(child.content ?? ""))
      .join("");
    if (fromTextNodes) {
      node.content = fromTextNodes;
    } else if (node.content && children.length === 0) {
      node.components = [{ type: "textnode", content: String(node.content) }];
    }
  }
  if (Array.isArray(node.components)) {
    node.components.forEach(normalizeTextComponentForLoad);
  }
}

function prepareProjectDataForLoad(raw: Record<string, unknown>) {
  const payload = stripProjectData(raw);
  if (!Object.keys(payload).length) {
    return payload;
  }
  const data = cloneProjectData(payload);
  delete data.custom;
  const pages = data.pages;
  if (Array.isArray(pages)) {
    pages.forEach((page: any) => {
      (page?.frames || []).forEach((frame: any) => {
        normalizeTextComponentForLoad(frame?.component);
      });
    });
  }
  return data;
}

export default defineComponent({
  name: "SiteEditor",
  data() {
    return {
      editor: null as any,
      site: null as any,
      homePage: null as any,
      error: "",
      publishMessage: "",
      publishing: false,
      loadingEditor: true,
      dirty: false,
      editorReady: false,
      suppressDirty: true,
      saveState: "idle" as "idle" | "saving" | "saved" | "error",
      dirtyTimer: null as number | null,
      dirtyTrackingAttached: false,
      bootstrapGeneration: 0,
      initGeneration: 0,
      dirtyMarkHandler: null as (() => void) | null
    };
  },
  async mounted() {
    await this.bootstrap();
  },
  beforeUnmount() {
    if (this.dirtyTimer) {
      clearTimeout(this.dirtyTimer);
      this.dirtyTimer = null;
    }
    this.destroyEditor();
  },
  methods: {
    destroyEditor() {
      this.detachDirtyTracking();
      if (this.editor) {
        this.editor.destroy();
        this.editor = null;
      }
      this.editorReady = false;
      const refs = [
        this.$refs.editorHost,
        this.$refs.blocksPanel,
        this.$refs.layersPanel,
        this.$refs.stylesPanel,
        this.$refs.devicesPanel
      ] as HTMLElement[];
      refs.forEach((el) => {
        if (el) {
          el.innerHTML = "";
        }
      });
    },
    formatError(err: unknown, fallback: string) {
      const e = err as { response?: { data?: unknown }; message?: string };
      const data = e?.response?.data;
      if (data && typeof data === "object" && data !== null && "message" in data) {
        return String((data as { message?: string }).message || fallback);
      }
      if (typeof data === "string") {
        try {
          const parsed = JSON.parse(data);
          if (parsed?.message) {
            return String(parsed.message);
          }
        } catch (_parseErr) {
          if (data.trim()) {
            return data;
          }
        }
      }
      return e?.message || fallback;
    },
    attachDirtyTracking() {
      if (!this.editor || this.dirtyTrackingAttached) {
        return;
      }
      const mark = () => this.scheduleDirtyMark();
      this.dirtyMarkHandler = mark;
      // Fires once per undo-stack change — avoids update/style loops when adding blocks.
      this.editor.on("change:changesCount", mark);
      this.dirtyTrackingAttached = true;
    },
    detachDirtyTracking() {
      if (!this.editor || !this.dirtyMarkHandler) {
        this.dirtyTrackingAttached = false;
        this.dirtyMarkHandler = null;
        return;
      }
      const mark = this.dirtyMarkHandler;
      this.editor.off("change:changesCount", mark);
      this.dirtyMarkHandler = null;
      this.dirtyTrackingAttached = false;
    },
    scheduleDirtyMark() {
      if (this.suppressDirty) {
        return;
      }
      if (this.dirtyTimer) {
        return;
      }
      this.dirtyTimer = window.setTimeout(() => {
        this.dirtyTimer = null;
        this.dirty = true;
        if (this.saveState === "saved") {
          this.saveState = "idle";
        }
      }, 250);
    },
    resolveProjectData(page: any) {
      const fromApi = normalizeProjectData(page?.draftDocument || page?.draft_document);
      if (fromApi && Array.isArray(fromApi.pages) && fromApi.pages.length) {
        return fromApi;
      }
      if (!this.site || !page?.id) {
        return fromApi;
      }
      try {
        const cached = sessionStorage.getItem(draftCacheKey(this.site.id, page.id));
        if (cached) {
          const parsed = normalizeProjectData(JSON.parse(cached));
          if (parsed?.pages?.length) {
            return parsed;
          }
        }
      } catch (_err) {
        // ignore cache read errors
      }
      return fromApi;
    },
    async bootstrap() {
      const generation = ++this.bootstrapGeneration;
      this.error = "";
      this.dirty = false;
      this.saveState = "idle";
      this.loadingEditor = true;
      this.editorReady = false;
      try {
        const siteId = this.$route.params.id;
        const response = await getGhlSite(siteId);
        if (generation !== this.bootstrapGeneration) {
          return;
        }
        this.site = response.data?.site || null;
        this.homePage = (this.site?.pages || []).find((page: any) => page.path === "/") || this.site?.pages?.[0] || null;
        if (!this.homePage) {
          this.error = "No home page found for this site.";
          return;
        }

        this.destroyEditor();
        await this.$nextTick();
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        const projectData = this.resolveProjectData(this.homePage);
        await this.initEditor(projectData, generation);
        if (generation !== this.bootstrapGeneration) {
          return;
        }
        this.saveState = "saved";
        this.dirty = false;
      } catch (err) {
        this.error = this.formatError(err, "Failed to load site");
      } finally {
        this.loadingEditor = false;
      }
    },
    finishInitialLoad(initGeneration: number) {
      if (initGeneration !== this.initGeneration || !this.editor) {
        return;
      }
      this.suppressDirty = false;
      this.attachDirtyTracking();
    },
    selectInitialPage() {
      if (!this.editor?.Pages) {
        return;
      }
      const pages = this.editor.Pages.getAll();
      if (!pages?.length) {
        return;
      }
      const preferred =
        pages.find((page: any) => page.get && page.get("type") === "main") || pages[0];
      const pageId = preferred.getId ? preferred.getId() : preferred.id;
      if (pageId != null) {
        this.editor.Pages.select(pageId);
      }
    },
    loadProjectIntoEditor(projectData: Record<string, unknown>, initGeneration: number) {
      if (!this.editor || initGeneration !== this.initGeneration) {
        return;
      }
      this.suppressDirty = true;
      this.detachDirtyTracking();

      const payload = prepareProjectDataForLoad(projectData);
      if (Object.keys(payload).length) {
        this.editor.loadProjectData(payload, { preserveIds: true });
        this.selectInitialPage();
        if (this.editor.UndoManager && typeof this.editor.UndoManager.clear === "function") {
          this.editor.UndoManager.clear();
        }
      }
      window.requestAnimationFrame(() => {
        this.finishInitialLoad(initGeneration);
      });
    },
    async initEditor(projectData: Record<string, unknown>, bootstrapGeneration: number) {
      const initGeneration = ++this.initGeneration;
      const host = this.$refs.editorHost as HTMLElement | undefined;
      const blocksPanel = this.$refs.blocksPanel as HTMLElement | undefined;
      const layersPanel = this.$refs.layersPanel as HTMLElement | undefined;
      const stylesPanel = this.$refs.stylesPanel as HTMLElement | undefined;
      const devicesPanel = this.$refs.devicesPanel as HTMLElement | undefined;

      if (!host || !blocksPanel || !layersPanel || !stylesPanel || !devicesPanel) {
        throw new Error("Editor containers are not ready");
      }

      host.innerHTML = "";
      blocksPanel.innerHTML = "";
      layersPanel.innerHTML = "";
      stylesPanel.innerHTML = "";
      devicesPanel.innerHTML = "";

      await this.$nextTick();
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      const workspace = host.closest(".site-editor-workspace") as HTMLElement | null;
      const editorHeight = Math.max(
        400,
        workspace?.clientHeight || window.innerHeight - 110
      );

      if (bootstrapGeneration !== this.bootstrapGeneration) {
        return;
      }

      this.suppressDirty = true;
      this.editor = grapesjs.init({
        container: host,
        height: `${editorHeight}px`,
        width: "auto",
        fromElement: false,
        storageManager: false,
        noticeOnUnload: false,
        showOffsets: false,
        panels: {
          defaults: [
            {
              id: "panel-devices",
              el: devicesPanel,
              buttons: [
                {
                  id: "device-desktop",
                  label: "Desktop",
                  command: "set-device-desktop",
                  active: true,
                  togglable: false
                },
                {
                  id: "device-tablet",
                  label: "Tablet",
                  command: "set-device-tablet",
                  togglable: false
                },
                {
                  id: "device-mobile",
                  label: "Mobile",
                  command: "set-device-mobile",
                  togglable: false
                }
              ]
            }
          ]
        },
        blockManager: {
          appendTo: blocksPanel
        },
        layerManager: {
          appendTo: layersPanel
        },
        styleManager: {
          appendTo: stylesPanel
        },
        plugins: [gjsBlocksBasic],
        pluginsOpts: {
          "gjs-blocks-basic": {
            flexGrid: false
          }
        }
      });

      if (bootstrapGeneration !== this.bootstrapGeneration || initGeneration !== this.initGeneration) {
        this.destroyEditor();
        return;
      }

      this.loadProjectIntoEditor(projectData, initGeneration);
      this.editorReady = true;
    },
    getEditorProjectData() {
      if (!this.editor || typeof this.editor.getProjectData !== "function") {
        return null;
      }
      return this.editor.getProjectData();
    },
    cacheDraftLocally(draft_document: Record<string, unknown>) {
      if (!this.site || !this.homePage) {
        return;
      }
      try {
        sessionStorage.setItem(
          draftCacheKey(this.site.id, this.homePage.id),
          JSON.stringify(draft_document)
        );
      } catch (_err) {
        // ignore cache write errors
      }
    },
    async saveDraft() {
      if (!this.editor || !this.homePage || !this.site) {
        return false;
      }
      this.error = "";
      const draft_document = this.getEditorProjectData();
      if (!draft_document || typeof draft_document !== "object") {
        this.saveState = "error";
        this.error = "Editor is not ready to save yet.";
        return false;
      }
      this.saveState = "saving";
      try {
        const response = await updateGhlSitePage(this.site.id, this.homePage.id, { draft_document });
        const page = response.data?.page;
        if (!page) {
          throw new Error("Save response missing page data");
        }
        const savedDraft = normalizeProjectData(
          page.draftDocument || page.draft_document || draft_document
        );
        this.homePage = {
          ...this.homePage,
          ...page,
          draftDocument: savedDraft,
          draft_document: savedDraft
        };
        this.cacheDraftLocally(savedDraft as Record<string, unknown>);
        this.saveState = "saved";
        this.dirty = false;
        return true;
      } catch (err) {
        console.error(err);
        this.saveState = "error";
        this.error = this.formatError(err, "Failed to save draft");
        return false;
      }
    },
    openPreview() {
      if (!this.site || !this.homePage) return;
      this.error = "";
      if (this.dirty) {
        this.error = "Save your changes before previewing.";
        return;
      }

      const url = `${previewGhlSitePageUrl(this.site.id, this.homePage.id)}?t=${Date.now()}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    async publish() {
      if (!this.site || !this.homePage) return;
      this.publishMessage = "";
      this.error = "";
      if (this.dirty) {
        this.error = "Save your changes before publishing.";
        return;
      }
      this.publishing = true;
      try {
        const response = await publishGhlSitePage(this.site.id, this.homePage.id);
        const publicUrl = response.data?.publicUrl;
        this.publishMessage = publicUrl
          ? `Published. Live URL: ${publicUrl}`
          : "Published successfully.";
        const siteResponse = await getGhlSite(this.site.id);
        this.site = siteResponse.data?.site || this.site;
        this.homePage = (this.site?.pages || []).find((page: any) => page.path === "/") || this.homePage;
      } catch (err) {
        this.error = this.formatError(err, "Publish failed");
      } finally {
        this.publishing = false;
      }
    }
  }
});
